import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input, Spinner } from "@porchpass/ui";
import type { DtoAdminSearchBorrowerApplication } from "@porchpass/api-client";
import MainLayout from "src/layouts/MainLayout/MainLayout";
import { applicationExternalId } from "src/api/preApproval/mappers";
import { searchBorrowers } from "src/api/preApproval";
import { PreApprovalEmptyState } from "../components/PreApprovalEmptyState";
import { BorrowerResultList } from "./BorrowerResultList";
import { SearchEmptyState } from "./SearchEmptyState";

const QUERY_PARAM = "q";

export function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get(QUERY_PARAM) ?? "";

  const [inputValue, setInputValue] = useState(urlQuery);
  const [results, setResults] = useState<DtoAdminSearchBorrowerApplication[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setInputValue(urlQuery);
  }, [urlQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = inputValue.trim();
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const previousQuery = prev.get(QUERY_PARAM) ?? "";

          if (trimmed.length < 2) {
            if (!previousQuery) {
              return prev;
            }
            next.delete(QUERY_PARAM);
            return next;
          }

          if (trimmed === previousQuery) {
            return prev;
          }

          next.set(QUERY_PARAM, trimmed);
          return next;
        },
        { replace: true },
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, setSearchParams]);

  useEffect(() => {
    const trimmed = urlQuery.trim();
    if (!trimmed || trimmed.length < 2) {
      setResults([]);
      setHasSearched(false);
      setIsLoading(false);
      setSearchError(null);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setHasSearched(true);
    setSearchError(null);
    searchBorrowers(trimmed, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setResults(data);
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setResults([]);
          setSearchError(
            error instanceof Error
              ? error.message
              : "Unable to search borrowers right now.",
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [urlQuery, retryCount]);

  const handleOpenWorkspace = useCallback(
    (borrower: DtoAdminSearchBorrowerApplication) => {
      const externalId = applicationExternalId(borrower);
      if (!externalId) {
        return;
      }
      const returnTo = searchParams.toString()
        ? `/pre-approval?${searchParams.toString()}`
        : "/pre-approval";
      navigate(
        `/pre-approval/${encodeURIComponent(externalId)}/conditions/customer`,
        { state: { borrower, returnTo } },
      );
    },
    [navigate, searchParams],
  );

  const showNoResults =
    hasSearched && !isLoading && !searchError && results.length === 0;
  const showResults =
    hasSearched && !isLoading && !searchError && results.length > 0;
  const showInitialHint = !hasSearched && !isLoading;

  return (
    <MainLayout page="pre-approval" isCollapsed={false}>
      <div className="flex w-full flex-col gap-4">
        <Input
          label="Search by name, email, loan number, or personal ID..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="w-full"
        />

        {showInitialHint && (
          <p className="text-sm text-[#697083]">
            Enter at least 2 characters to search borrowers.
          </p>
        )}

        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-12 text-[#697083]">
            <Spinner size="s" />
            <span className="text-sm">Searching borrowers...</span>
          </div>
        )}

        {searchError && !isLoading && (
          <PreApprovalEmptyState
            title="Borrower search unavailable"
            description={searchError}
            actionLabel="Try again"
            onAction={() => setRetryCount((count) => count + 1)}
          />
        )}

        {showNoResults && (
          <PreApprovalEmptyState
            title="No borrowers found"
            description="Try a different name, email address, loan number, or personal ID."
          />
        )}

        {showResults && (
          <BorrowerResultList
            results={results}
            onOpenWorkspace={handleOpenWorkspace}
          />
        )}

        {showInitialHint && (
          <div className="w-full">
            <SearchEmptyState />
          </div>
        )}
      </div>
    </MainLayout>
  );
}
